import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

interface RetryButtonProps {
  onRetry: () => Promise<void> | void;
  disabled?: boolean;
  className?: string;
  variant?: 'default' | 'outline' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  children?: React.ReactNode;
  maxRetries?: number;
  retryDelay?: number;
  checkNetwork?: boolean;
}

export function RetryButton({
  onRetry,
  disabled = false,
  className,
  variant = 'outline',
  size = 'sm',
  children,
  maxRetries = 3,
  retryDelay = 1000,
  checkNetwork = true,
}: RetryButtonProps) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const networkStatus = useNetworkStatus();

  const handleRetry = async () => {
    if (isRetrying || disabled) return;
    
    // Check network status if enabled
    if (checkNetwork && !networkStatus.isOnline) {
      return;
    }

    setIsRetrying(true);
    
    try {
      await onRetry();
      setRetryCount(0); // Reset on success
    } catch (error) {
      console.error('Retry failed:', error);
      setRetryCount(prev => prev + 1);
      
      // Add delay before allowing next retry
      if (retryCount < maxRetries - 1) {
        setTimeout(() => {
          setIsRetrying(false);
        }, retryDelay);
        return;
      }
    }
    
    setIsRetrying(false);
  };

  const isDisabled = disabled || 
    isRetrying || 
    retryCount >= maxRetries ||
    (checkNetwork && !networkStatus.isOnline);

  return (
    <Button
      onClick={handleRetry}
      disabled={isDisabled}
      variant={variant}
      size={size}
      className={cn('flex items-center gap-2', className)}
    >
      {isRetrying ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <RefreshCw className="h-4 w-4" />
      )}
      {children || (isRetrying ? 'Retrying...' : 'Retry')}
      {retryCount > 0 && retryCount < maxRetries && (
        <span className="text-xs opacity-70">
          ({retryCount}/{maxRetries})
        </span>
      )}
    </Button>
  );
}

// Specialized retry button for API operations
export function ApiRetryButton({
  onRetry,
  className,
}: {
  onRetry: () => Promise<void>;
  className?: string;
}) {
  return (
    <RetryButton
      onRetry={onRetry}
      className={className}
      maxRetries={3}
      retryDelay={2000}
      checkNetwork={true}
    >
      Try Again
    </RetryButton>
  );
}

// Retry button for streaming operations
export function StreamingRetryButton({
  onRetry,
  className,
}: {
  onRetry: () => Promise<void>;
  className?: string;
}) {
  return (
    <RetryButton
      onRetry={onRetry}
      className={className}
      variant="ghost"
      size="sm"
      maxRetries={5}
      retryDelay={1000}
      checkNetwork={true}
    >
      Reconnect
    </RetryButton>
  );
}